/**
 * Review screen (Screen 5) — reviewer checklist, 3-step stepper, approval gate.
 *
 * Shows:
 * - Stepper: Draft (done) → Under review (current) → Published (upcoming)
 * - Reviewer checklist: Structural check / Scenario coverage / Domain owner assigned
 * - "Approve & send to publish" button (navigates to P6c's Publish screen)
 * - AI-proposed edits queue (Task 6 implements this section)
 *
 * Note: This component renders only the checklist and stepper. AI-edit queue
 * rendering is in Task 6.
 */

import { useMemo, useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import type { Sandbox } from '../state/atlasApiTypes.js';
import type { SandboxOp } from '../state/sandboxOps.js';
import { applyOps, normalizeSandboxOps } from '../state/sandboxOps.js';
import { overlayDiff } from '../state/sandboxOverlay.js';
import { navigateSimulateSandbox, navigate } from '../state/hashRouter.js';
import { diffObject } from '@atlas-manifest/diff';
import { impactTree } from '../graph/pathfind.js';
import { entityById, surfaceById, cloudFunctionById, eventTypeById, pathByPath } from '../state/selectors.js';
import { DiffView } from './DiffView.js';
import './ReviewView.css';

export interface ReviewViewProps {
  manifest: AtlasManifest;
  sandbox: Sandbox;
  onNavigateBack: () => void;
  onApproveAndPublish: () => void;
  onGeneratePrototype: (
    overlayManifest: AtlasManifest,
  ) => Promise<
    | { ok: true; documentCount: number; emulatorUrl: string }
    | { ok: false; error: string }
  >;
  onSaveSandbox?: (sandboxId: string, name: string, ops: SandboxOp[]) => Promise<void>;
  isAdmin?: boolean;
  adminQueue?: Array<Sandbox & { ownerUid: string }>;
}

export function ReviewView(props: ReviewViewProps): React.ReactElement {
  const { manifest, sandbox, onNavigateBack, onApproveAndPublish, onGeneratePrototype, onSaveSandbox, isAdmin, adminQueue } = props;
  const [showDiff, setShowDiff] = useState(false);

  const diff = useMemo(() => overlayDiff(manifest, sandbox.ops as SandboxOp[]), [manifest, sandbox.ops]);

  // Gate the AI-proposed-edits section on any op having a confidence score.
  // Uses per-op confidence model exclusively; legacy sandbox.aiConfidence field
  // is no longer used for gating to prevent semantic drift.
  const hasAiDraftedOps = normalizeSandboxOps(sandbox.ops).some((entry) => entry.confidence !== undefined);

  // Checklist items: Structural check, Scenario coverage, Domain owner assigned.
  const structuralCheckPass = diff.brokenRefs.length === 0;

  const [prototypeState, setPrototypeState] = useState<
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'success'; documentCount: number; emulatorUrl: string }
    | { status: 'error'; error: string }
  >({ status: 'idle' });

  const overlayManifest = useMemo(() => {
    const ops = normalizeSandboxOps(sandbox.ops)
      .filter((entry) => entry.state === 'accepted')
      .map((entry) => entry.op);
    return applyOps(manifest, ops);
  }, [manifest, sandbox.ops]);

  const hasProposedEntities = overlayManifest.entities.some((e) => e.lifecycle === 'proposed');

  const handleGeneratePrototype = async () => {
    setPrototypeState({ status: 'loading' });
    const result = await onGeneratePrototype(overlayManifest);
    setPrototypeState(
      result.ok
        ? { status: 'success', documentCount: result.documentCount, emulatorUrl: result.emulatorUrl }
        : { status: 'error', error: result.error },
    );
  };

  const handleRejectAiEdit = async (): Promise<void> => {
    if (!onSaveSandbox) return;
    const acceptedOps = normalizeSandboxOps(sandbox.ops)
      .filter((e) => e.state === 'accepted')
      .map((e) => e.op);
    await onSaveSandbox(sandbox.id, sandbox.name, acceptedOps);
  };

  return (
    <div className="review-view">
      <div className="detail-topbar">
        <button className="btn btn-ghost" onClick={onNavigateBack}>
          ← {sandbox.name}
        </button>
        <h1 className="detail-title">Review</h1>
      </div>

      {/* Admin review queue */}
      {isAdmin && adminQueue && adminQueue.length > 0 && (
        <div className="admin-review-queue" style={{ padding: 'var(--sp-md)', backgroundColor: 'var(--bg2)', borderBottom: '1px solid var(--border)' }}>
          <div className="section-label">Admin review queue</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-sm)' }}>
            {adminQueue.map((item) => (
              <div
                key={`${item.ownerUid}/${item.id}`}
                style={{
                  padding: 'var(--sp-md)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: 'var(--bg1)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 'var(--sp-md)' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.name}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text2)' }}>Owner: {item.ownerUid}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
                      Updated {new Date(item.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      navigate({ kind: 'review', sandboxId: item.id, ownerUid: item.ownerUid });
                    }}
                  >
                    Review →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3-step stepper */}
      <div className="review-stepper">
        <div className="review-step done">
          <span className="review-step-dot">✓</span>
          Draft
        </div>
        <div className="review-step-line done"></div>
        <div className="review-step current">
          <span className="review-step-dot">2</span>
          Under review
        </div>
        <div className="review-step-line"></div>
        <div className="review-step">
          <span className="review-step-dot">3</span>
          Published
        </div>
      </div>

      <div className="detail-body" style={{ gridTemplateColumns: '1fr 280px' }}>
        <div>
          <div className="section-label">Reviewer checklist</div>
          <div className="card" style={{ marginBottom: 'var(--sp-xl)' }}>
            <div className="review-checklist">
              <div className={`review-check-item ${structuralCheckPass ? 'pass' : 'fail'}`}>
                <span className="review-check-icon">
                  {structuralCheckPass ? '✓' : '✕'}
                </span>
                <span className="review-check-label">Structural check</span>
                <span className="review-check-meta">
                  overlayDiff() · {diff.brokenRefs.length} broken refs
                </span>
              </div>

              <div className="review-check-item pass">
                <span className="review-check-icon">✓</span>
                <span className="review-check-label">Scenario coverage</span>
                <span className="review-check-meta">0% · no traces yet</span>
              </div>

              <div className="review-check-item pass">
                <span className="review-check-icon">✓</span>
                <span className="review-check-label">Domain owner assigned</span>
                <span className="review-check-meta mono">@assign-me</span>
              </div>
            </div>
          </div>

          <button className="btn btn-ghost btn-sm" onClick={() => navigateSimulateSandbox(sandbox.id)}>
            Simulate this draft
          </button>

          <button className="btn btn-primary" onClick={onApproveAndPublish}>
            Approve &amp; send to publish →
          </button>

          {structuralCheckPass && hasProposedEntities && (
            <div className="card prototype-cta" style={{ marginTop: 'var(--sp-lg)' }}>
              <p className="section-label">Clickable prototype</p>
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text2)' }}>
                Structural checks pass for this sandbox overlay — a clickable prototype
                (real Atlas UI, seeded local Firestore emulator) can be generated from it.
              </p>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleGeneratePrototype}
                disabled={prototypeState.status === 'loading'}
              >
                {prototypeState.status === 'loading' ? 'Generating…' : 'Generate clickable prototype'}
              </button>

              {prototypeState.status === 'error' && (
                <p className="publish-error">{prototypeState.error}</p>
              )}

              {prototypeState.status === 'success' && (
                <div className="prototype-success">
                  <p>
                    ✓ Seeded {prototypeState.documentCount} document{prototypeState.documentCount !== 1 ? 's' : ''}.
                    Emulator UI: <code>{prototypeState.emulatorUrl}</code>
                  </p>
                  <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
                    Run <code>firebase emulators:start --import=emulator-data</code>, then set{' '}
                    <code>VITE_USE_EMULATORS=true</code> when starting the frontend dev server.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* AI-proposed edits queue */}
          {hasAiDraftedOps && (
            <>
              <div className="section-label" style={{ marginTop: 'var(--sp-xl)' }}>
                Also awaiting your review — AI-proposed edits
              </div>

              <div className="ai-review-item">
                <span className="ai-star">✨</span>
                <div className="ai-review-item-body">
                  <div className="ai-review-item-title">
                    {/* For MVP: show first op summary */}
                    {sandbox.ops.length === 1 ? 'AI-suggested change' : `${sandbox.ops.length} AI-suggested changes`}
                  </div>
                  <div className="ai-review-item-meta">
                    via Ask Atlas · {sandbox.ops.length} op{sandbox.ops.length !== 1 ? 's' : ''} ·
                    source: ai-gapfill
                  </div>
                  {sandbox.aiConfidence !== undefined && (
                    <div className="confbar" style={{ margin: '6px 0 0' }}>
                      <span className="confbar-label">Confidence</span>
                      <div className="confbar-track" style={{ maxWidth: '100px' }}>
                        <div
                          className="confbar-fill"
                          style={{
                            width: `${Math.round(sandbox.aiConfidence * 100)}%`,
                            background: 'var(--caution)',
                          }}
                        ></div>
                      </div>
                      <span className="mono">{Math.round(sandbox.aiConfidence * 100)}%</span>
                    </div>
                  )}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowDiff((v) => !v)}>
                  View diff
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => void handleRejectAiEdit()}>
                  Reject
                </button>
                <button className="btn btn-primary btn-sm" onClick={onApproveAndPublish}>
                  Approve & publish
                </button>
              </div>

              <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)', marginTop: '8px' }}>
                Single-field AI edits publish directly on approval — no separate publish walk-through for a 1-op change.
              </p>

              {/* Render field-level diffs for each update-* op */}
              <div style={{ marginTop: 'var(--sp-lg)' }}>
                {sandbox.ops
                  .map((entry) => normalizeSandboxOps([entry])[0])
                  .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined && entry.op.kind.startsWith('update-'))
                  .map((entry, i) => {
                    const op = entry.op;

                    // Resolve "before" via the matching selector for op.kind
                    let before: Record<string, unknown> | undefined;
                    if (op.kind === 'update-entity') {
                      before = entityById(manifest, (op as any).entityId) as Record<string, unknown> | undefined;
                    } else if (op.kind === 'update-surface') {
                      before = surfaceById(manifest, (op as any).surfaceId) as Record<string, unknown> | undefined;
                    } else if (op.kind === 'update-cloud-function') {
                      before = cloudFunctionById(manifest, (op as any).name) as Record<string, unknown> | undefined;
                    } else if (op.kind === 'update-event') {
                      before = eventTypeById(manifest, (op as any).type) as Record<string, unknown> | undefined;
                    } else if (op.kind === 'update-path') {
                      before = pathByPath(manifest, (op as any).path) as Record<string, unknown> | undefined;
                    }

                    if (!before) return null;

                    // Compute "after" by merging the patch fields on top of the before object
                    const after = { ...before, ...op };
                    const changes = diffObject(before, after);

                    return (
                      <div key={i} className="review-patch-diff" style={{ marginBottom: 'var(--sp-lg)', padding: 'var(--sp-md)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                        <div className="section-label">Proposed changes</div>
                        {changes.length === 0 ? (
                          <p style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>No field changes detected</p>
                        ) : (
                          <div>
                            {changes.map((c) => (
                              <div key={c.field} className="review-patch-diff-row" style={{ marginTop: 'var(--sp-sm)', fontSize: 'var(--fs-xs)' }}>
                                <span className="mono" style={{ fontWeight: 600 }}>{c.field}</span>
                                <span style={{ color: 'var(--text3)', margin: '0 var(--sp-xs)' }}>·</span>
                                <span style={{ color: 'var(--text3)' }}>
                                  {c.change === 'added' ? 'added' : c.change === 'removed' ? 'removed' : 'changed'}
                                </span>
                                {(c.change === 'changed' || c.change === 'added') && (
                                  <>
                                    <span style={{ color: 'var(--text3)', margin: '0 var(--sp-xs)' }}>→</span>
                                    <span className="mono">{JSON.stringify(c.to)}</span>
                                  </>
                                )}
                                {c.change === 'removed' && (
                                  <>
                                    <span style={{ color: 'var(--text3)', margin: '0 var(--sp-xs)' }}>was</span>
                                    <span className="mono">{JSON.stringify(c.from)}</span>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>

              {/* Blast-radius context for entity edits and other cross-references */}
              <div style={{ marginTop: 'var(--sp-lg)' }}>
                {sandbox.ops
                  .map((entry) => normalizeSandboxOps([entry])[0])
                  .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined && entry.op.kind === 'update-entity')
                  .map((entry, i) => {
                    const op = entry.op as any;
                    const entity = entityById(manifest, op.entityId);
                    if (!entity) return null;

                    const impact = impactTree(manifest, op.entityId);

                    return (
                      <div key={i} className="review-context-blast" style={{ marginBottom: 'var(--sp-lg)', padding: 'var(--sp-md)', backgroundColor: 'var(--background-secondary)', borderRadius: 'var(--radius-sm)' }}>
                        <div className="section-label">Blast radius: entities that reference "{entity.name}"</div>
                        {impact.length === 0 ? (
                          <p style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>No downstream entities</p>
                        ) : (
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text2)' }}>
                            <p style={{ marginBottom: 'var(--sp-sm)' }}>
                              {impact.length} entit{impact.length === 1 ? 'y' : 'ies'} affected across {new Set(impact.map((n) => n.domain)).size} domain{new Set(impact.map((n) => n.domain)).size === 1 ? '' : 's'}
                            </p>
                            <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                              {impact.slice(0, 5).map((node) => (
                                <li key={node.entityId} style={{ marginBottom: 'var(--sp-xs)', paddingLeft: 'var(--sp-md)' }}>
                                  <span>{node.entityId}</span>
                                  <span style={{ color: 'var(--text3)', marginLeft: 'var(--sp-sm)' }}>depth {node.depth}</span>
                                  {node.crossDomain && <span style={{ color: 'var(--caution)', marginLeft: 'var(--sp-xs)' }}>↗ cross-domain</span>}
                                </li>
                              ))}
                            </ul>
                            {impact.length > 5 && (
                              <p style={{ color: 'var(--text3)', marginTop: 'var(--sp-sm)' }}>+{impact.length - 5} more</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>

              {/* Cross-reference context for surface, cloud function, event, and path edits */}
              <div style={{ marginTop: 'var(--sp-lg)' }}>
                {sandbox.ops
                  .map((entry) => normalizeSandboxOps([entry])[0])
                  .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined && entry.op.kind === 'update-surface')
                  .map((entry, i) => {
                    const op = entry.op as any;
                    const surface = surfaceById(manifest, op.surfaceId);
                    if (!surface) return null;

                    return (
                      <div key={i} className="review-context-surface" style={{ marginBottom: 'var(--sp-lg)', padding: 'var(--sp-md)', backgroundColor: 'var(--background-secondary)', borderRadius: 'var(--radius-sm)' }}>
                        <div className="section-label">Surface context: "{surface.id}"</div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text2)' }}>
                          {surface.type && <p style={{ marginBottom: 'var(--sp-xs)' }}>Type: {surface.type}</p>}
                          {surface.domain && <p style={{ marginBottom: 'var(--sp-xs)' }}>Domain: {surface.domain}</p>}
                          {surface.hub && <p style={{ marginBottom: 'var(--sp-xs)' }}>Hub: {surface.hub}</p>}
                          {surface.description && <p style={{ marginBottom: 'var(--sp-xs)' }}>Description: {surface.description}</p>}
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div style={{ marginTop: 'var(--sp-lg)' }}>
                {sandbox.ops
                  .map((entry) => normalizeSandboxOps([entry])[0])
                  .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined && entry.op.kind === 'update-cloud-function')
                  .map((entry, i) => {
                    const op = entry.op as any;
                    const fn = cloudFunctionById(manifest, op.name);
                    if (!fn) return null;

                    return (
                      <div key={i} className="review-context-function" style={{ marginBottom: 'var(--sp-lg)', padding: 'var(--sp-md)', backgroundColor: 'var(--background-secondary)', borderRadius: 'var(--radius-sm)' }}>
                        <div className="section-label">Cloud function context: "{fn.name}"</div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text2)' }}>
                          {fn.reads && fn.reads.length > 0 && (
                            <p style={{ marginBottom: 'var(--sp-xs)' }}>Reads: {fn.reads.join(', ')}</p>
                          )}
                          {fn.writes && fn.writes.length > 0 && (
                            <p style={{ marginBottom: 'var(--sp-xs)' }}>Writes: {fn.writes.join(', ')}</p>
                          )}
                          {(fn as any).emitsEvents && (fn as any).emitsEvents.length > 0 && (
                            <p style={{ marginBottom: 'var(--sp-xs)' }}>Emits: {(fn as any).emitsEvents.join(', ')}</p>
                          )}
                          {fn.description && <p style={{ marginBottom: 'var(--sp-xs)' }}>Description: {fn.description}</p>}
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div style={{ marginTop: 'var(--sp-lg)' }}>
                {sandbox.ops
                  .map((entry) => normalizeSandboxOps([entry])[0])
                  .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined && entry.op.kind === 'update-event')
                  .map((entry, i) => {
                    const op = entry.op as any;
                    const event = eventTypeById(manifest, op.type);
                    if (!event) return null;

                    return (
                      <div key={i} className="review-context-event" style={{ marginBottom: 'var(--sp-lg)', padding: 'var(--sp-md)', backgroundColor: 'var(--background-secondary)', borderRadius: 'var(--radius-sm)' }}>
                        <div className="section-label">Event context: "{event.type}"</div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text2)' }}>
                          {event.carriedEntities && event.carriedEntities.length > 0 && (
                            <p style={{ marginBottom: 'var(--sp-xs)' }}>Carries: {event.carriedEntities.join(', ')}</p>
                          )}
                          {event.category && <p style={{ marginBottom: 'var(--sp-xs)' }}>Category: {event.category}</p>}
                          {event.description && <p style={{ marginBottom: 'var(--sp-xs)' }}>Description: {event.description}</p>}
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div style={{ marginTop: 'var(--sp-lg)' }}>
                {sandbox.ops
                  .map((entry) => normalizeSandboxOps([entry])[0])
                  .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined && entry.op.kind === 'update-path')
                  .map((entry, i) => {
                    const op = entry.op as any;
                    const path = pathByPath(manifest, op.path);
                    if (!path) return null;

                    return (
                      <div key={i} className="review-context-path" style={{ marginBottom: 'var(--sp-lg)', padding: 'var(--sp-md)', backgroundColor: 'var(--background-secondary)', borderRadius: 'var(--radius-sm)' }}>
                        <div className="section-label">Firestore path context: "{path.path}"</div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text2)' }}>
                          {path.entityId && <p style={{ marginBottom: 'var(--sp-xs)' }}>Entity: {path.entityId}</p>}
                          {path.retention && <p style={{ marginBottom: 'var(--sp-xs)' }}>Retention: {path.retention}</p>}
                          {path.securityRuleNote && <p style={{ marginBottom: 'var(--sp-xs)' }}>Security: {path.securityRuleNote}</p>}
                        </div>
                      </div>
                    );
                  })}
              </div>

              {showDiff && (
                <div style={{ marginTop: 'var(--sp-lg)' }}>
                  <DiffView
                    manifest={applyOps(manifest, normalizeSandboxOps(sandbox.ops).map((e) => e.op))}
                    baselineManifest={manifest}
                    subtitle="Changes proposed by this sandbox"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <aside className="right-panel">
          <h3 className="overlay-panel-title">Why review at all?</h3>
          <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text2)', lineHeight: 1.6 }}>
            Checks above prove the draft is <i>internally consistent</i> — not that
            it's the <i>right</i> design. That call is the domain owner's, human- or AI-authored
            either way.
          </p>
        </aside>
      </div>
    </div>
  );
}
